import { AddIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { isFreeUser } from 'src/constants/plans';
import { useBoardStore } from 'src/features/boards';
import { useFileStore } from 'src/features/files';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useAuth } from 'src/shared/hooks/use-auth';
import { shallow } from 'zustand/shallow';

import { useKanbanStore } from '../../store';
import { KanbanState } from '../../store/types';
// import { KanbanItem } from '../Item';
import { KanbanItem_new } from '../Item';
import { CreateTask } from '../Item/NewTask/CreateTask';
import { KanbanColumnHeader } from './Header';
import { Column } from './types';
export interface KanbanColumnProps {
  column: Column;
  index: number;
}

export const KanbanColumn = ({ column, index }: KanbanColumnProps) => {
  const { onChangeCurrentSelectedItem, currentSelectedItem, isLoading } = useKanbanStore(
    boardSelector,
    shallow
  );
  const { isPublicFile } = useAuth((s) => ({
    isPublicFile: s.isPublicFile,
  }));
  const router = useRouter();

  const { isReadOnly } = useFileAccess();

  const [istop, setIstop] = useState(false);
  const { access, totalTaskCount, actions } = useBoardStore((s) => ({
    access: s.access,
    totalTaskCount: s.totalTaskCount,
    actions: s.actions,
  }));

  const handleTaskViewOpen = (
    columnId: KanbanState['currentSelectedItem']['columnId'],
    index: KanbanState['currentSelectedItem']['itemIndex'],
    mode: KanbanState['currentSelectedItem']['mode'],
    top?: boolean,
    selectedTemplateRef?: string
  ) => {
    onChangeCurrentSelectedItem({
      isOpen: true,
      columnId,
      itemIndex: index,
      mode: mode,
      top,
      selectedTemplateRef,
    });
  };

  useEffect(() => {
    if (!currentSelectedItem.isOpen) {
      setIstop(false);
      currentSelectedItem.top = false;
    } else if (currentSelectedItem.isOpen && istop) {
      setIstop(true);
      currentSelectedItem.top = true;
    }
  }, [currentSelectedItem]);
  const authData = useAuth((s) => s.userDetails);
  const currentPlan = authData?.plan?.type ?? '';

  const canAddTask = useMemo(
    () => !isFreeUser(currentPlan) || totalTaskCount < 10,
    [totalTaskCount]
  );

  return (
    <Draggable
      draggableId={column.id}
      index={index}
      isDragDisabled={isReadOnly || access === 'view' || isLoading}
    >
      {(provided) => (
        <div className="flex" {...provided.draggableProps} ref={provided.innerRef}>
          <div
            className="w-284 mr-16 overflow-y-scroll no-scrollbar"
            style={{ height: `${isPublicFile ? 'calc((100vh - 185px)' : '100vh'}` }}
          >
            <div className="sticky top-0 bg-gray-100 z-15" {...provided.dragHandleProps}>
              <KanbanColumnHeader
                key={column.id}
                data={{
                  id: column.id,
                  name: column?.name,
                  color: column?.color,
                  count: column?.items.length,
                  index: index,
                  onAddNewTask: (
                    columnId: KanbanState['currentSelectedItem']['columnId'],
                    index: KanbanState['currentSelectedItem']['itemIndex'],
                    mode: KanbanState['currentSelectedItem']['mode'],
                    top: boolean,
                    selectedTemplateRef?: string
                  ) => {
                    setIstop(true);
                    /* selectedTemplateRef variable is the selected template id while creating a new task*/
                    handleTaskViewOpen(column.id, undefined, 'add', true, selectedTemplateRef);
                  },
                }}
              />
            </div>
            <div className="flex flex-col">
              {currentSelectedItem.columnId === column.id && istop && canAddTask && <CreateTask />}
            </div>
            <Droppable droppableId={column.id}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={cn({ 'h-full': column?.items.length < 4 })}
                >
                  {column?.items.map((item, index) => (
                    <div className="mb-20" key={item.id}>
                      <KanbanItem_new key={item.id} columnId={column.id} itemIndex={index} />
                    </div>
                  ))}
                  {currentSelectedItem.mode === 'add' &&
                  currentSelectedItem.columnId === column.id &&
                  currentSelectedItem.isOpen &&
                  !istop &&
                  canAddTask ? (
                    <div className="mt-16">
                      <CreateTask />
                    </div>
                  ) : (
                    <></>
                  )}
                  {provided.placeholder}
                  <>
                    {router?.query?.type === 'agile' ? (
                      <></>
                    ) : (
                      <>
                        {column?.items.length && !isReadOnly && access !== 'view' && canAddTask ? (
                          <Tippy content={<span>Create new task</span>}>
                            <button
                              className="flex"
                              tabIndex={-1}
                              onClick={() => {
                                setIstop(false);
                                handleTaskViewOpen(column.id, undefined, 'add');
                              }}
                            >
                              <AddIcon wrapperClass="addtask text-gray-500 mt-12" />
                            </button>
                          </Tippy>
                        ) : !canAddTask ? (
                          <Tippy content={<span>Upgrade to unlock</span>}>
                            <button className="flex" tabIndex={-1}>
                              <AddIcon wrapperClass="addtask text-gray-400 text-sm" />
                            </button>
                          </Tippy>
                        ) : (
                          <></>
                        )}
                      </>
                    )}
                  </>
                </div>
              )}
            </Droppable>
          </div>
        </div>
      )}
    </Draggable>
  );
};

const boardSelector = (s: KanbanState) => ({
  onChangeCurrentSelectedItem: s.onChangeCurrentSelectedItem,
  isReadOnly: s.isReadOnly,
  currentSelectedItem: s.currentSelectedItem,
  isLoading: s.isLoading,
});
